import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { toast } from "sonner";
import { FileCode, AlertCircle, CheckCircle2, Copy, Users } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { listRecipientsForTag, registerRecipientForTag } from "@/lib/recipient-tags.functions";

export const Route = createFileRoute("/_authenticated/analyzer")({
  component: AnalyzerPage,
  head: () => ({ meta: [{ title: "HTML Analyzer — InboxPulse" }] }),
});

const TRIGGER_WORDS = [
  "free",
  "guarantee",
  "winner",
  "act now",
  "limited time",
  "click here",
  "100%",
  "risk-free",
  "urgent",
  "cash",
  "earn money",
  "no obligation",
  "buy now",
  "cheap",
  "discount",
  "save up to",
];

function analyze(html: string) {
  const text = html.replace(/<[^>]+>/g, " ").toLowerCase();
  const found = TRIGGER_WORDS.filter((w) => text.includes(w));
  const linkCount = (html.match(/<a\s/gi) || []).length;
  const imgCount = (html.match(/<img\s/gi) || []).length;
  const textLen = text.replace(/\s+/g, " ").trim().length;
  const ratio = imgCount === 0 ? 1 : Math.min(1, textLen / (imgCount * 200));
  const findings: { severity: "info" | "warn" | "error"; message: string }[] = [];
  if (found.length)
    findings.push({ severity: "warn", message: `Spam trigger words: ${found.join(", ")}` });
  if (linkCount > 5) findings.push({ severity: "warn", message: `High link count (${linkCount})` });
  if (ratio < 0.4) findings.push({ severity: "error", message: "Low text-to-image ratio" });
  if (!html.includes("<html")) findings.push({ severity: "info", message: "Missing <html> tag" });
  const score = Math.min(100, found.length * 8 + (linkCount > 5 ? 15 : 0) + (ratio < 0.4 ? 25 : 0));
  return { findings, score, triggerWords: found, linkCount, imageTextRatio: ratio };
}

function AnalyzerPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [html, setHtml] = useState("");
  const [result, setResult] = useState<ReturnType<typeof analyze> | null>(null);
  const [openTag, setOpenTag] = useState<string | null>(null);
  const [recipInput, setRecipInput] = useState("");
  const fetchRecipients = useServerFn(listRecipientsForTag);
  const addRecipient = useServerFn(registerRecipientForTag);

  const { data: history } = useQuery({
    queryKey: ["analyses"],
    queryFn: async () =>
      (
        await supabase
          .from("html_analyses")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(10)
      ).data ?? [],
  });

  const save = useMutation({
    mutationFn: async () => {
      const r = analyze(html);
      setResult(r);
      const tagToken =
        "mt_" +
        (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2))
          .replace(/-/g, "")
          .slice(0, 16);
      const { error } = await supabase.from("html_analyses").insert({
        user_id: user!.id,
        name: name || "Untitled analysis",
        html_content: html,
        spam_score: r.score,
        findings: r.findings,
        image_text_ratio: r.imageTextRatio,
        link_count: r.linkCount,
        trigger_words: r.triggerWords,
        tag_token: tagToken,
      });
      if (error) throw error;
      return tagToken;
    },
    onSuccess: (tagToken) => {
      toast.success("Analysis saved");
      qc.invalidateQueries({ queryKey: ["analyses"] });
      if (tagToken) setOpenTag(tagToken);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const { data: recipData, refetch: refetchRecipients } = useQuery({
    queryKey: ["recipients-for-tag", openTag],
    queryFn: () => fetchRecipients({ data: { tag: openTag! } }),
    enabled: !!openTag,
  });

  const snippet = (tag: string) =>
    `<meta name="x-mailtag" content="${tag}">\n<!-- InboxPulse: Empfänger via registerRecipientForTag({tag:"${tag}", recipient_email:"…"}) verknüpfen -->`;

  const copy = (t: string) => {
    navigator.clipboard.writeText(t).then(() => toast.success("Kopiert"));
  };

  const addRecip = async () => {
    if (!openTag || !recipInput.trim()) return;
    try {
      await addRecipient({
        data: { tag: openTag, recipient_email: recipInput.trim() },
      });
      setRecipInput("");
      refetchRecipients();
      toast.success("Empfänger verknüpft");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="p-8">
      <PageHeader
        title="HTML Analyzer"
        description="Spot spam triggers, bad ratios, and risky links before you send."
      />
      <HelpPanel
        title="Hilfe: HTML-Mail auf Spam-Fallen prüfen"
        what={
          <>
            Du <b>klebst hier deinen Mail-HTML rein</b>, und das System sucht nach Dingen, die
            Spam-Filter triggern: zu viele Bilder, verdächtige Wörter, kaputte Links, fehlender
            Plain-Text-Teil.
          </>
        }
        why={
          <>
            Spam-Filter geben Punkte für jedes Warnzeichen. Erst ab einem bestimmten Wert landest du
            im Spam. Hier siehst du vorher, wo du nachbessern solltest.
          </>
        }
        how={[
          <>HTML aus deinem Versand-Tool kopieren und einfügen.</>,
          <>
            <b>Analysieren</b> klicken.
          </>,
          <>Bericht lesen: rote Treffer beheben, gelbe ggf. optimieren.</>,
          <>Neu analysieren bis Score grün ist.</>,
        ]}
        tips={[
          <>Bild-zu-Text-Verhältnis: max. 40% Bild. Reine Bild-Mails landen fast immer im Spam.</>,
          <>Wörter wie GRATIS, JETZT KAUFEN, !!! — vermeiden oder umschreiben.</>,
          <>Jeder Link sollte zum sichtbaren Text passen (kein "klicke hier" → unbekannte URL).</>,
        ]}
        glossary={[
          {
            term: "Spam-Score",
            def: "Punkte-Konto: jeder Spam-Verdacht gibt Punkte. Über Schwelle = Spam-Ordner.",
          },
          {
            term: "Plain-Text-Teil",
            def: "Nur-Text-Version der Mail (ohne Bilder/HTML). Pflicht — fehlt sie, gibt es Spam-Punkte.",
          },
        ]}
      />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6 bg-card/60 border-border">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Q2 outreach v3"
              />
            </div>
            <div className="space-y-2">
              <Label>HTML</Label>
              <Textarea
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                rows={14}
                className="font-mono text-xs"
                placeholder="<html>..."
              />
            </div>
            <Button
              onClick={() => save.mutate()}
              disabled={!html || save.isPending}
              className="w-full bg-[image:var(--gradient-primary)] hover:opacity-90"
            >
              Analyze & save
            </Button>
          </div>
        </Card>

        <Card className="p-6 bg-card/60 border-border">
          {!result ? (
            <div className="h-full flex items-center justify-center text-center text-muted-foreground py-20">
              <div>
                <FileCode className="size-10 mx-auto mb-3" />
                <p>Paste HTML and click analyze to see the spam-risk breakdown.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between">
                <h3 className="font-semibold">Spam risk score</h3>
                <div className="text-4xl font-semibold">
                  {result.score}
                  <span className="text-base text-muted-foreground">/100</span>
                </div>
              </div>
              <div className="mt-4 h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full ${result.score < 30 ? "bg-success" : result.score < 60 ? "bg-warning" : "bg-destructive"}`}
                  style={{ width: `${result.score}%` }}
                />
              </div>
              <div className="mt-6 space-y-3">
                {result.findings.length === 0 && (
                  <div className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="size-4 text-success mt-0.5" /> Looks clean.
                  </div>
                )}
                {result.findings.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <AlertCircle
                      className={`size-4 mt-0.5 ${f.severity === "error" ? "text-destructive" : f.severity === "warn" ? "text-warning" : "text-muted-foreground"}`}
                    />
                    <span>{f.message}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-border grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-muted-foreground">Links</div>
                  <div className="text-base font-semibold">{result.linkCount}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Triggers</div>
                  <div className="text-base font-semibold">{result.triggerWords.length}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Text ratio</div>
                  <div className="text-base font-semibold">{result.imageTextRatio.toFixed(2)}</div>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {!!history?.length && (
        <div className="mt-8">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Recent analyses
          </h2>
          <Card className="bg-card/60 border-border overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-border first:border-t-0">
                    <td className="px-4 py-3 font-medium">{h.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(h.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {h.tag_token ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      {h.tag_token && (
                        <Button variant="ghost" size="sm" onClick={() => setOpenTag(h.tag_token!)}>
                          <Users className="size-3 mr-1" /> Empfänger
                        </Button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`text-sm font-semibold ${h.spam_score < 30 ? "text-success" : h.spam_score < 60 ? "text-warning" : "text-destructive"}`}
                      >
                        {h.spam_score}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {openTag && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <Card className="bg-card border-border w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold">Tag-Empfänger</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Tag: <span className="font-mono">{openTag}</span>
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOpenTag(null)}>
                Schließen
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">In dein HTML einbauen</Label>
              <div className="flex gap-2">
                <Textarea
                  readOnly
                  rows={2}
                  className="font-mono text-xs"
                  value={snippet(openTag)}
                />
                <Button variant="outline" size="sm" onClick={() => copy(snippet(openTag))}>
                  <Copy className="size-3" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Empfänger-Adresse verknüpfen</Label>
              <div className="flex gap-2">
                <Input
                  value={recipInput}
                  onChange={(e) => setRecipInput(e.target.value)}
                  placeholder="empfaenger@example.com"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addRecip();
                  }}
                />
                <Button onClick={addRecip}>Hinzufügen</Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">
                Verknüpfte Empfänger ({recipData?.recipients.length ?? 0})
              </Label>
              <div className="border border-border rounded-md overflow-hidden">
                {!recipData?.recipients.length ? (
                  <div className="p-4 text-xs text-muted-foreground text-center">
                    Noch keine Empfänger – füge oben Adressen hinzu, damit Bounces zugeordnet werden
                    können.
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-muted/30 text-muted-foreground uppercase">
                      <tr>
                        <th className="px-3 py-2 text-left">Empfänger</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Bounce-Grund</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipData.recipients.map((r) => (
                        <tr key={r.id} className="border-t border-border">
                          <td className="px-3 py-2">{r.recipient_email}</td>
                          <td className="px-3 py-2">
                            <span
                              className={
                                r.status === "bounced"
                                  ? "text-destructive font-medium"
                                  : r.status === "delivered"
                                    ? "text-success"
                                    : "text-muted-foreground"
                              }
                            >
                              {r.status}
                              {r.bounce_type ? ` (${r.bounce_type})` : ""}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground max-w-[280px] truncate">
                            {r.bounce_reason ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
